"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  Shield, Target, Building2, Briefcase, Brain, Bot, Database, Plug,
  LayoutDashboard, ChevronDown, Sparkles, Settings, Mail, FileText,
  Users, Lock, Store, ShoppingCart, BarChart3, Layers, MessageSquare,
  Globe, CreditCard, Activity, Network, GitBranch, Smartphone, Tablet,
  Monitor, Radio, ShieldCheck, Upload, ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight, Code2, BookOpen, Key, Webhook, Terminal,
  Package, FileCode, Layout, Palette, Image, Tag, Warehouse, Hash,
  Truck, Contact, UserCog, MapPin, Fingerprint, Bell, LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { menuApi } from "@/lib/api-client";

// Icon name → component mapping
const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Shield, Target, Building2, Briefcase, Brain, Bot, Database, Plug,
  LayoutDashboard, Sparkles, Settings, Mail, FileText, Users, Lock,
  Store, ShoppingCart, BarChart3, Layers, MessageSquare, Globe,
  CreditCard, Activity, Network, GitBranch, Smartphone, Tablet,
  Monitor, Radio, ShieldCheck, Upload, Code2, BookOpen, Key, Webhook,
  Terminal, Package, FileCode, Layout, Palette, Image, Tag, Warehouse,
  Hash, Truck, Contact, UserCog, MapPin, Fingerprint, Bell,
};

// biz_nav → group label mapping
const bizNavLabels: Record<string, { label: string; color: string }> = {
  "ts-governance": { label: "天枢·定规则", color: "#8b5cf6" },
  "lingyao": { label: "灵耀·管租户", color: "#3b82f6" },
  "middle": { label: "中庭·跑业务", color: "#059669" },
  "yaoguang": { label: "瑶光·出智能", color: "#b8943f" },
  "lingxi": { label: "灵犀·做呈现", color: "#22d3ee" },
  "kaitian": { label: "开天·即时交互", color: "#e11d48" },
  "workbench": { label: "工作台", color: "#6366f1" },
};

interface NavChild {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}
interface NavItem {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  color?: string;
  children?: NavChild[];
  external?: boolean;
}

// Fallback static menu (used when API fails)
const fallbackGroups: { label: string; code: string; color: string; items: NavItem[] }[] = [
  {
    label: "天枢·定规则", code: "/P", color: "#8b5cf6",
    items: [
      { title: "平台治理", icon: Shield, href: "/P/governance" },
      { title: "权限中心", icon: Lock, href: "/P/auth" },
      { title: "日志审计", icon: Activity, href: "/P/audit" },
      { title: "系统安全", icon: ShieldCheck, href: "/P/security" },
    ],
  },
  {
    label: "灵耀·管租户", code: "/F", color: "#3b82f6",
    items: [
      { title: "租户管理", icon: Building2, href: "/F/tenant" },
      { title: "角色管理", icon: Shield, href: "/F/role" },
      { title: "用户管理", icon: Users, href: "/F/user" },
    ],
  },
  {
    label: "中庭·跑业务", code: "/B", color: "#059669",
    items: [
      { title: "业务概览", icon: LayoutDashboard, href: "/B/overview" },
      { title: "集成管理", icon: Plug, href: "/B/integrations" },
      { title: "运营监控", icon: BarChart3, href: "/B/monitor" },
    ],
  },
  {
    label: "瑶光·出智能", code: "/A", color: "#b8943f",
    items: [
      { title: "大模型管理", icon: Brain, href: "/A/models" },
      { title: "智能体", icon: Bot, href: "/A/agents" },
    ],
  },
];

interface AppSidebarProps {
  collapsed?: boolean;
  onToggle?: () => void;
}

export function AppSidebar({ collapsed = false, onToggle }: AppSidebarProps) {
  const pathname = usePathname();
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [navGroups, setNavGroups] = useState<typeof fallbackGroups>(fallbackGroups);
  const [menuLoaded, setMenuLoaded] = useState(false);

  // Fetch dynamic menu from backend
  useEffect(() => {
    menuApi.tree().then((res) => {
      const menus = (res as any).data || res;
      if (!menus || menus.length === 0) return;

      // Group menus by biz_nav
      const grouped: Record<string, any[]> = {};
      menus.forEach((m: any) => {
        const biz = m.bizNav || m.biz_nav || "other";
        if (!grouped[biz]) grouped[biz] = [];
        grouped[biz].push(m);
      });

      // Sort within each group by sort_order
      Object.values(grouped).forEach(items => items.sort((a, b) => (a.sortOrder || a.sort_order || 0) - (b.sortOrder || b.sort_order || 0)));

      // Build navGroups
      const groups: typeof fallbackGroups = [];
      const bizNavOrder = ["ts-governance", "lingyao", "middle", "yaoguang", "lingxi", "kaitian", "workbench"];

      bizNavOrder.forEach(bizNav => {
        if (!grouped[bizNav]) return;
        const meta = bizNavLabels[bizNav] || { label: bizNav, color: "#64748b" };
        const items: NavItem[] = grouped[bizNav].map((m: any) => ({
          title: m.title || "未命名",
          icon: iconMap[m.icon] || LayoutDashboard,
          href: m.path || undefined,
        }));
        groups.push({ label: meta.label, code: `/${bizNav[0].toUpperCase()}`, color: meta.color, items });
      });

      if (groups.length > 0) {
        setNavGroups(groups);
        // Default expand first two groups
        const defaults: Record<string, boolean> = {};
        groups.slice(0, 2).forEach(g => { defaults[g.label] = true; });
        setExpandedGroups(defaults);
      }
      setMenuLoaded(true);
    }).catch(() => {
      setMenuLoaded(true);
      // Keep fallback
    });
  }, []);

  const toggleGroup = (label: string) => {
    setExpandedGroups((prev) => {
      if (prev[label]) return { ...prev, [label]: false };
      const expandedCount = Object.values(prev).filter(Boolean).length;
      if (expandedCount >= 2) {
        const expandedKeys = Object.keys(prev).filter((k) => prev[k]);
        return { ...prev, [label]: true, [expandedKeys[0]]: false };
      }
      return { ...prev, [label]: true };
    });
  };

  const isActive = (href: string) => {
    if (!href) return false;
    if (href === "/") return pathname === "/";
    return pathname.startsWith(href);
  };

  return (
    <aside
      className={cn(
        "relative flex flex-col h-full border-r border-border/50 transition-all duration-300",
        collapsed ? "w-[68px]" : "w-[178px]"
      )}
      style={{ background: "#ffffff" }}
    >
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {navGroups.map((group) => (
          <div key={group.label} className="mb-1">
            {collapsed ? (
              <div className="flex flex-col items-center gap-1 py-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center border border-border/30 hover:border-stellar-gold/50 hover:bg-stellar-gold/5 transition-all cursor-pointer"
                  style={{ backgroundColor: `${group.color}15` }}
                  title={group.label}
                >
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: group.color }} />
                </div>
                <span className="text-[9px] text-muted-foreground text-center leading-tight max-w-[60px] truncate">
                  {group.label.split("·")[0].trim()}
                </span>
              </div>
            ) : (
              <>
                <button
                  onClick={() => toggleGroup(group.label)}
                  className="flex items-center gap-2 w-full px-3 py-2 text-[13px] font-semibold text-foreground/90 hover:text-foreground transition-colors"
                >
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: group.color }} />
                  {group.label}
                  <ChevronDown className={cn("w-3.5 h-3.5 ml-auto transition-transform", expandedGroups[group.label] ? "" : "-rotate-90")} />
                </button>
                {expandedGroups[group.label] && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => (
                      <div key={item.title}>
                        {item.href ? (
                          <Link
                            href={item.href}
                            className={cn(
                              "relative flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[12px] transition-all duration-200 group",
                              isActive(item.href)
                                ? "bg-accent text-accent-foreground"
                                : "text-muted-foreground hover:text-foreground hover:bg-gray-50"
                            )}
                          >
                            {isActive(item.href) && <div className="sidebar-active-indicator" />}
                            <item.icon className={cn("w-4 h-4 shrink-0 transition-colors", isActive(item.href) ? "text-stellar-gold" : "group-hover:text-foreground")} />
                            <span>{item.title}</span>
                          </Link>
                        ) : (
                          <button
                            onClick={() => toggleGroup(item.title)}
                            className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-gray-50 transition-all w-full"
                          >
                            <item.icon className="w-4 h-4 shrink-0" />
                            <span>{item.title}</span>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        ))}
        {!menuLoaded && (
          <div className="px-3 py-2 text-[10px] text-muted-foreground animate-pulse">加载菜单...</div>
        )}
      </nav>

      <button
        onClick={() => onToggle?.()}
        className="absolute top-1/2 -right-3 w-6 h-6 rounded-full bg-white border border-border/60 shadow-sm flex items-center justify-center hover:bg-gray-50 hover:border-stellar-gold/40 transition-all z-50"
        title={collapsed ? "展开侧边栏" : "收起侧边栏"}
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronLeft className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>
    </aside>
  );
}
